import React, {FC} from 'react'
import {observer} from 'mobx-react'
import {useTranslation} from 'react-i18next'
import {MapFormState} from './MapSettingsModal'
import styled from 'styled-components'
import {Space, theme} from '../../ui'
import {Switch} from '@material-ui/core'
import TextField from "@material-ui/core/TextField";
import {
    ModalText,
    InputWrapper,
    InputContainer,
    InputErrorText,
    ToggleGroup,
    ToggleRow,
} from './styles'

const StyledTextField = styled(TextField)`
  width: 100%;

  .MuiOutlinedInput-root {
    border-radius: 8px;
    background: ${theme.color.white};
  }
  .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${theme.color.confirmButton}80;
  }
  .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${theme.color.confirmButton};
  }
`

const GeoDataExample = styled.div`
  /* @noflip */ direction: ltr;
  text-align: left;
  color: ${theme.color.grey};
  font-style: italic;
  font-size: 12.5px;
  background: ${theme.color.whiteBackground};
  border: 1px dashed ${theme.color.lightGrayBackground};
  border-radius: 8px;
  padding: 8px 12px;
  margin: 4px 0 10px;
  overflow-x: auto;
  white-space: nowrap;
`

const ToolLink = styled.a`
  display: inline-block;
  margin: 10px 2px 0;
  color: ${theme.color.confirmButton};
  font-weight: 600;
  font-size: 14px;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

interface MapSectionProps {
    mapFormState: MapFormState
    mapFormValidState: any
    isMapFormChanged: boolean,
    onMapSettingsSave: () => void
    onMapSettingsLocationToggleEnable: (value: boolean) => void
    onMapSettingsEmergencyToggleEnable: (value: boolean) => void
    onMapSettingsToggleEnable: (value: boolean) => void
    onMapSettingsChangeAccuracy: (value: boolean) => void
    onMapSettingsChangeFromKM: (value: boolean) => void
    onMapSettingsChangeToKM: (value: boolean) => void
    onMapSettingsChangeLat: (value: boolean) => void
    onMapSettingsChangeLng: (value: boolean) => void
    onMapSettingsChangeZoom: (value: boolean) => void
    onCommunityChangeInfo: (value: boolean) => void
    onCommunityChangeGeoData: (value: boolean) => void

}

const MapSection: FC<MapSectionProps> = (props) => {
    const {t} = useTranslation('mapSettings')
    const {
        mapFormState,
        mapFormValidState,
        onCommunityChangeInfo,
        onMapSettingsToggleEnable,
        onMapSettingsLocationToggleEnable,
        onMapSettingsEmergencyToggleEnable,
        onMapSettingsChangeAccuracy,
        onMapSettingsChangeFromKM,
        onMapSettingsChangeToKM,
        onMapSettingsChangeLat,
        onMapSettingsChangeLng,
        onMapSettingsChangeZoom,
        onCommunityChangeGeoData,
    } = props

    return (
        <>
            <ToggleGroup>
                <ToggleRow>
                    <ModalText>{t`showMapTab`}:</ModalText>
                    <Switch
                        color="primary"
                        value={mapFormState.enabled}
                        onChange={({target}) => {
                            onMapSettingsToggleEnable(target.checked)
                        }}
                        checked={mapFormState.enabled}
                    />
                </ToggleRow>
                <ToggleRow>
                    <ModalText>{t`showEmergencyBtn`}:</ModalText>
                    <Switch
                        color="primary"
                        value={mapFormState.emergencyEnabled}
                        onChange={({target}) => {
                            onMapSettingsEmergencyToggleEnable(target.checked)
                        }}
                        checked={mapFormState.emergencyEnabled}
                    />
                </ToggleRow>
                <ToggleRow>
                    <ModalText>{t`showLocationRequestToogle`}:</ModalText>
                    <Switch
                        color="primary"
                        value={mapFormState.locationEnabled}
                        onChange={({target}) => {
                            onMapSettingsLocationToggleEnable(target.checked)
                        }}
                        checked={mapFormState.locationEnabled}
                    />
                </ToggleRow>
            </ToggleGroup>

            <InputWrapper>
                <InputContainer style={{ width: '100%' }}>
                    <StyledTextField
                        id={'lat'}
                        label={t`lat`}
                        variant="outlined"
                        value={mapFormState.lat}
                        onChange={({target}) => {
                            onMapSettingsChangeLat(target.value)
                        }}
                    />
                    {
                        !mapFormValidState['lat'].valid &&
                        <InputErrorText>{t`validationError`}</InputErrorText>
                    }
                </InputContainer>
                <InputContainer style={{ width: '100%' }}>
                    <StyledTextField
                        id={'lng'}
                        variant="outlined"
                        label={t`lng`}
                        value={mapFormState.lng}
                        onChange={({target}) => {
                            onMapSettingsChangeLng(target.value)
                        }}
                    />
                    {
                        !mapFormValidState['lng'].valid &&
                        <InputErrorText>{t`validationError`}</InputErrorText>
                    }
                </InputContainer>
            </InputWrapper>
            <Space/>

            <InputWrapper>
                <InputContainer style={{ width: '100%' }}>
                    <StyledTextField
                        id={'accuracy'}
                        variant="outlined"
                        label={t`accuracy`}
                        value={mapFormState.accuracy}
                        onChange={({target}) => {
                            onMapSettingsChangeAccuracy(target.value)
                        }}
                    />
                    {
                        !mapFormValidState['accuracy'].valid &&
                        <InputErrorText>{t`validationError`}</InputErrorText>
                    }
                </InputContainer>
                <InputContainer style={{ width: '100%' }}>
                    <StyledTextField
                        id={'zoom'}
                        variant="outlined"
                        label={t`zoom`}
                        value={mapFormState.zoom}
                        onChange={({target}) => {
                            onMapSettingsChangeZoom(target.value)
                        }}
                    />
                    {
                        !mapFormValidState['zoom'].valid &&
                        <InputErrorText>{t`validationError`}</InputErrorText>
                    }
                </InputContainer>
            </InputWrapper>
            <Space/>

            <InputWrapper>
                <InputContainer style={{ width: '100%' }}>
                    <StyledTextField
                        id={'from-km'}
                        variant="outlined"
                        label={t`fromKM`}
                        value={mapFormState.fromKM}
                        onChange={({target}) => {
                            onMapSettingsChangeFromKM(target.value)
                        }}
                    />
                    {
                        !mapFormValidState['fromKM'].valid &&
                        <InputErrorText>{t`validationError`}</InputErrorText>
                    }
                </InputContainer>
                <InputContainer style={{ width: '100%' }}>
                    <StyledTextField
                        id={'to-km'}
                        variant="outlined"
                        label={t`toKM`}
                        value={mapFormState.toKM}
                        onChange={({target}) => {
                            onMapSettingsChangeToKM(target.value)
                        }}
                    />
                    {
                        !mapFormValidState['toKM'].valid &&
                        <InputErrorText>{t`validationError`}</InputErrorText>
                    }
                </InputContainer>
                <InputContainer style={{ width: '100%' }}>
                    <StyledTextField
                        id={'more-info'}
                        variant="outlined"
                        label={t`moreInformation`}
                        value={mapFormState.moreInformation}
                        onChange={({target}) => {
                            onCommunityChangeInfo(target.value)
                        }}
                    />
                </InputContainer>
            </InputWrapper>
            <Space/>

            <GeoDataExample>
            {JSON.stringify({"coordinates": [[[-72.2777867,42.9282112],[-72.2781086,42.9255873]]]})}
            </GeoDataExample>
            <InputWrapper>
                <InputContainer style={{ width: '100%' }}>
                    <StyledTextField
                        multiline
                        minRows={3}
                        id={'geo-data'}
                        variant="outlined"
                        label={t`geoData`}
                        value={mapFormState.geoData}
                        inputProps={{ style: { direction: 'ltr', textAlign: 'left' } }}
                        onChange={({target}) => {
                            onCommunityChangeGeoData(target.value)
                        }}
                    />
                </InputContainer>
            </InputWrapper>
            <ToolLink target="_blank" href="https://www.keene.edu/campus/maps/tool/">{t`open_boundary_tool`}</ToolLink>

        </>
    )
}

export default observer(MapSection)
